/**
 * Pure timing & clock conversion functions for MIDI / PPQ arithmetic
 * Decoupled from React and DOM
 */

import { TimeSignature, TransportPosition } from '../types/midi';

export const DEFAULT_PPQ = 480;

/**
 * Returns ticks for one beat according to time signature.
 * Beat type 4 = quarter note = PPQ ticks.
 * Beat type 8 = eighth note = PPQ / 2 ticks.
 */
export function getTicksPerBeat(ppq: number = DEFAULT_PPQ, beatType: number = 4): number {
  return Math.round(ppq * (4 / beatType));
}

/**
 * Returns total ticks per measure for the given time signature.
 */
export function getTicksPerMeasure(
  timeSig: TimeSignature,
  ppq: number = DEFAULT_PPQ
): number {
  const ticksPerBeat = getTicksPerBeat(ppq, timeSig.beatType);
  return timeSig.beats * ticksPerBeat;
}

/**
 * Convert ticks to milliseconds based on BPM and PPQ
 */
export function ticksToMs(ticks: number, bpm: number, ppq: number = DEFAULT_PPQ): number {
  if (bpm <= 0) return 0;
  const msPerBeat = 60000 / bpm;
  return (ticks / ppq) * msPerBeat;
}

/**
 * Convert milliseconds to ticks based on BPM and PPQ
 */
export function msToTicks(ms: number, bpm: number, ppq: number = DEFAULT_PPQ): number {
  if (bpm <= 0) return 0;
  const msPerBeat = 60000 / bpm;
  return Math.round((ms / msPerBeat) * ppq);
}

/**
 * Convert an absolute tick position into { measure, beat, tick, totalSeconds }
 * Measures and beats are 1-indexed for display.
 */
export function ticksToPosition(
  ticks: number,
  timeSig: TimeSignature,
  bpm: number,
  ppq: number = DEFAULT_PPQ
): TransportPosition {
  const nonNegativeTicks = Math.max(0, ticks);
  const measureTicks = getTicksPerMeasure(timeSig, ppq);
  const beatTicks = getTicksPerBeat(ppq, timeSig.beatType);

  const measureZeroIndexed = Math.floor(nonNegativeTicks / measureTicks);
  const remTicks = nonNegativeTicks % measureTicks;

  const beatZeroIndexed = Math.floor(remTicks / beatTicks);
  const tickInBeat = Math.round(remTicks % beatTicks);

  const totalSeconds = ticksToMs(nonNegativeTicks, bpm, ppq) / 1000;

  return {
    ticks: nonNegativeTicks,
    measure: measureZeroIndexed + 1,
    beat: Math.min(beatZeroIndexed + 1, timeSig.beats),
    tick: tickInBeat,
    totalSeconds,
  };
}

/**
 * Convert measure, beat, tick back to absolute ticks
 */
export function positionToTicks(
  measure: number,
  beat: number,
  tick: number,
  timeSig: TimeSignature,
  ppq: number = DEFAULT_PPQ
): number {
  const measureTicks = getTicksPerMeasure(timeSig, ppq);
  const beatTicks = getTicksPerBeat(ppq, timeSig.beatType);
  return (Math.max(1, measure) - 1) * measureTicks + (Math.max(1, beat) - 1) * beatTicks + Math.max(0, tick);
}

/**
 * Format position as bar.beat.tick (e.g. "001.01.000")
 */
export function formatPosition(pos: TransportPosition): string {
  const barStr = String(pos.measure).padStart(3, '0');
  const beatStr = String(pos.beat).padStart(2, '0');
  const tickStr = String(pos.tick).padStart(3, '0');
  return `${barStr}:${beatStr}:${tickStr}`;
}

/**
 * Format seconds as mm:ss.ms
 */
export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${ms}`;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function getPitchName(pitch: number): string {
  if (pitch < 0 || pitch > 127) return `P${pitch}`;
  const noteName = NOTE_NAMES[pitch % 12];
  const octave = Math.floor(pitch / 12) - 1; // MIDI 60 is C4
  return `${noteName}${octave}`;
}

export function isBlackKey(pitch: number): boolean {
  const rem = pitch % 12;
  return rem === 1 || rem === 3 || rem === 6 || rem === 8 || rem === 10;
}
